import React, { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import FloatingADUWizard from '@/components/FloatingADUWizard';
import FloatingReserveButton from '@/components/FloatingReserveButton';
import CanonicalTag from '@/components/CanonicalTag';

const SpanishColonialADUDesignCampbell = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Spanish Colonial ADU Design Campbell - Authentic Historic Style",
    "description": "Professional Spanish Colonial ADU design and construction services in Campbell. Bringing historic charm and timeless elegance to your property. Free consultation available.",
    "url": "https://udaadu.com/spanish-colonial-adu-design-campbell",
    "mainEntity": {
      "@type": "Service",
      "name": "Spanish Colonial ADU Design Campbell",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      }
    }
  };

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/spanish-colonial-adu-design-campbell" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[600px] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/adu-consultation"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
        <Navigation />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                Spanish Colonial ADU Design Campbell
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto">
                Professional Spanish Colonial ADU design and construction services in Campbell. Bringing the timeless elegance and authentic charm of Spanish architecture to your property.
              </p>
              <Button 
                onClick={openCalendlyModal}
                size="lg" 
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-4 text-lg"
              >
                Get Free Consultation
              </Button>
            </div>
          </section>

          {/* Why Choose Section */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
                Why Choose Spanish Colonial ADU Design in Campbell?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-red-600">🏰</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Historic Elegance</h3>
                  <p className="text-gray-600">Captures the romantic charm and architectural heritage of Spanish colonial design with authentic details.</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-red-600">☀️</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Climate Ideal</h3>
                  <p className="text-gray-600">Perfectly suited to California's climate with courtyards, thick walls, and outdoor living integration.</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-red-600">🎨</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Artisan Craftsmanship</h3>
                  <p className="text-gray-600">Features handcrafted details, decorative tiles, and ironwork that showcase traditional Spanish artistry.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-16 px-4 bg-gradient-to-br from-red-50 to-orange-50">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
                Spanish Colonial ADU Design Features
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Red Tile Roofs</h3>
                  <p className="text-gray-600">Authentic clay roof tiles in traditional terra cotta red that provide character and weather protection.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Stucco Walls</h3>
                  <p className="text-gray-600">Textured stucco exteriors in warm earth tones that create the classic Spanish colonial appearance.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Arched Openings</h3>
                  <p className="text-gray-600">Graceful arched windows, doors, and passageways that define the Spanish colonial aesthetic.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Decorative Ironwork</h3>
                  <p className="text-gray-600">Hand-forged iron details for windows, balconies, and gates that add authentic Spanish character.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Courtyard Spaces</h3>
                  <p className="text-gray-600">Private outdoor courtyards and patios that bring the indoors and outdoors together seamlessly.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Talavera Tiles</h3>
                  <p className="text-gray-600">Hand-painted ceramic tiles for backsplashes, fountains, and decorative accents with traditional patterns.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Local Considerations */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
                Campbell Local Considerations
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Regional Heritage</h3>
                  <p className="text-gray-600 mb-4">Our Spanish Colonial ADUs in Campbell honor California's mission heritage while meeting modern needs:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Reflects Campbell's California mission history</li>
                    <li>Complements the area's historic architecture</li>
                    <li>Uses materials suited to the local climate</li>
                    <li>Integrates with drought-resistant landscaping</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Modern Compliance</h3>
                  <p className="text-gray-600 mb-4">Designed to meet contemporary standards while preserving authentic style:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Meets Campbell zoning and building codes</li>
                    <li>Incorporates modern energy efficiency</li>
                    <li>Optimized for the Mediterranean climate</li>
                    <li>Enhances property values and neighborhood character</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Cost Section */}
          <section className="py-16 px-4 bg-gradient-to-br from-red-50 to-orange-50">
            <div className="max-w-6xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-800">
                Spanish Colonial ADU Investment in Campbell
              </h2>
              <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
                Spanish Colonial ADUs typically range from $200,000 to $380,000 in Campbell, with custom tile work and architectural details. 
                The timeless style provides excellent long-term value and distinctive character.
              </p>
              <Button 
                onClick={openCalendlyModal}
                size="lg" 
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-4 text-lg"
              >
                Get Detailed Cost Estimate
              </Button>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-4 bg-gradient-to-r from-red-600 to-orange-600 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Build Your Spanish Colonial ADU in Campbell?
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Let's create an authentic, elegant space that captures the timeless beauty of Spanish colonial architecture for your Campbell property.
              </p>
              <Button 
                onClick={openCalendlyModal}
                size="lg" 
                variant="outline" 
                className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Schedule Free Consultation
              </Button>
            </div>
          </section>

          {/* Internal Links */}
          <section className="py-12 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">Explore More ADU Designs</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/modern-adu-design-campbell" className="text-red-600 hover:text-red-700 text-center p-4 rounded-lg hover:bg-red-50 transition-colors">
                  Modern ADU Design Campbell
                </Link>
                <Link to="/craftsman-adu-design-campbell" className="text-red-600 hover:text-red-700 text-center p-4 rounded-lg hover:bg-red-50 transition-colors">
                  Craftsman ADU Design Campbell
                </Link>
                <Link to="/transitional-adu-design-campbell" className="text-red-600 hover:text-red-700 text-center p-4 rounded-lg hover:bg-red-50 transition-colors">
                  Transitional ADU Design Campbell
                </Link>
                <Link to="/modern-farmhouse-adu-design-campbell" className="text-red-600 hover:text-red-700 text-center p-4 rounded-lg hover:bg-red-50 transition-colors">
                  Modern Farmhouse ADU Design Campbell
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingADUWizard />
        <FloatingReserveButton />
      </div>
    </>
  );
};

export default SpanishColonialADUDesignCampbell;